// ---------------------------------------------------------------------------
// cities/new-york.content.ts — New York City landing page content (9 locales)
// ---------------------------------------------------------------------------

import type { CityRecord } from './types';

export const NEW_YORK_CITY: CityRecord = {
  constants: {
    slug: 'hotel-software-new-york',
    stats: {
      hotelCount: '700+',
      avgRate: 'USD 295',
      occupancyRate: '86%',
      peakSeason: 'Sep - Dec',
    },
  },
  content: {
    /* ------------------------------------------------------------------ */
    /*  English                                                           */
    /* ------------------------------------------------------------------ */
    en: {
      cityName: 'New York',
      country: 'United States',
      heroTitle: 'Hotel Software for New York City Properties',
      heroSubtitle:
        'Cloud PMS built for NYC hotels, boutique properties, and extended-stay residences. USD billing, NYC hotel tax compliance, and union labor scheduling tools.',
      highlights: [
        {
          title: 'NYC Hotel Tax Compliance',
          description:
            'Automated calculation of NYC hotel room occupancy tax, NY state sales tax, and city surcharges on every folio.',
        },
        {
          title: 'Union Staff Scheduling',
          description:
            'Built-in scheduling tools that respect union labor rules, overtime tracking, and shift management for unionized NYC hotel staff.',
        },
        {
          title: 'High-Volume Check-In',
          description:
            'Handle 200+ check-ins per day with express kiosk mode, group arrivals management, and automated room assignment algorithms.',
        },
        {
          title: 'Revenue Management',
          description:
            'AI-driven dynamic pricing optimized for NYC demand patterns, event-based rate adjustments, and competitive rate shopping.',
        },
      ],
      propertyTypes: [
        'Full-Service Hotels',
        'Boutique Hotels',
        'Extended Stay',
        'Luxury Hotels',
        'Convention Hotels',
        'Aparthotels',
      ],
      popularAreas: [
        'Midtown Manhattan',
        'Times Square',
        'SoHo',
        'Brooklyn',
        'Financial District',
        'Upper East Side',
        'Chelsea',
      ],
      compliance: [
        'NYC hotel occupancy tax',
        'NY state sales tax automation',
        'ADA accessibility tracking',
        'NYC fire safety reporting',
        'PCI DSS payment compliance',
      ],
      faqItems: [
        {
          question: 'Does HotelStack.io calculate NYC hotel taxes automatically?',
          answer:
            'Yes. Our system automatically applies NYC hotel room occupancy tax ($2 per room per night plus 5.875%), NY state sales tax (4%), and Metropolitan Commuter Transportation surcharge to every booking.',
        },
        {
          question: 'Can it handle high check-in volumes during peak NYC events?',
          answer:
            'Absolutely. Our express check-in mode, group arrival processing, and automated room assignment can handle 200+ check-ins per day. Ideal for properties near convention centers and event venues.',
        },
        {
          question: 'Does it support union labor scheduling requirements?',
          answer:
            'Yes. HotelStack.io includes union-aware scheduling with configurable overtime rules, break requirements, seniority-based shift assignment, and compliance reporting for NYC hotel unions.',
        },
        {
          question: 'What US payment processors are supported?',
          answer:
            'We integrate with Stripe, Square, Heartland, and all major US payment processors. PCI DSS Level 1 compliant with tokenized card storage and automated PCI reporting.',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  Arabic                                                            */
    /* ------------------------------------------------------------------ */
    ar: {
      cityName: 'نيويورك',
      country: 'الولايات المتحدة',
      heroTitle: 'برنامج إدارة الفنادق لعقارات مدينة نيويورك',
      heroSubtitle:
        'نظام إدارة فندقي سحابي مصمم لفنادق نيويورك والعقارات البوتيكية والإقامات الطويلة. فوترة بالدولار الأمريكي، امتثال ضريبي لفنادق نيويورك، وأدوات جدولة العمالة النقابية.',
      highlights: [
        {
          title: 'امتثال ضريبة فنادق نيويورك',
          description:
            'حساب تلقائي لضريبة إشغال غرف فنادق نيويورك وضريبة المبيعات لولاية نيويورك والرسوم الإضافية على كل فاتورة.',
        },
        {
          title: 'جدولة الموظفين النقابيين',
          description:
            'أدوات جدولة مدمجة تراعي قواعد العمل النقابي وتتبع العمل الإضافي وإدارة المناوبات لموظفي الفنادق النقابيين في نيويورك.',
        },
        {
          title: 'تسجيل وصول بكميات كبيرة',
          description:
            'معالجة أكثر من ٢٠٠ عملية تسجيل وصول يوميًا مع وضع الكشك السريع وإدارة وصول المجموعات وخوارزميات تعيين الغرف الآلية.',
        },
        {
          title: 'إدارة الإيرادات',
          description:
            'تسعير ديناميكي مدعوم بالذكاء الاصطناعي ومحسّن لأنماط الطلب في نيويورك وتعديلات الأسعار حسب الأحداث ومقارنة الأسعار التنافسية.',
        },
      ],
      propertyTypes: [
        'فنادق كاملة الخدمات',
        'فنادق بوتيكية',
        'إقامة ممتدة',
        'فنادق فاخرة',
        'فنادق المؤتمرات',
        'شقق فندقية',
      ],
      popularAreas: [
        'ميدتاون مانهاتن',
        'تايمز سكوير',
        'سوهو',
        'بروكلين',
        'الحي المالي',
        'أبر إيست سايد',
        'تشيلسي',
      ],
      compliance: [
        'ضريبة إشغال فنادق نيويورك',
        'أتمتة ضريبة مبيعات ولاية نيويورك',
        'تتبع إمكانية الوصول ADA',
        'تقارير السلامة من الحرائق في نيويورك',
        'امتثال الدفع PCI DSS',
      ],
      faqItems: [
        {
          question: 'هل يحسب HotelStack.io ضرائب فنادق نيويورك تلقائيًا؟',
          answer:
            'نعم. يطبق نظامنا تلقائيًا ضريبة إشغال غرف فنادق نيويورك (٢ دولار لكل غرفة في الليلة بالإضافة إلى ٥٫٨٧٥%) وضريبة مبيعات ولاية نيويورك (٤%) ورسوم النقل الحضري على كل حجز.',
        },
        {
          question: 'هل يمكنه التعامل مع أحجام تسجيل الوصول العالية خلال فعاليات نيويورك؟',
          answer:
            'بالتأكيد. يمكن لوضع تسجيل الوصول السريع ومعالجة وصول المجموعات وتعيين الغرف الآلي التعامل مع أكثر من ٢٠٠ عملية تسجيل وصول يوميًا.',
        },
        {
          question: 'هل يدعم متطلبات جدولة العمل النقابي؟',
          answer:
            'نعم. يتضمن HotelStack.io جدولة تراعي النقابات مع قواعد عمل إضافي قابلة للتكوين ومتطلبات الاستراحة وتعيين المناوبات بالأقدمية وتقارير الامتثال.',
        },
        {
          question: 'ما معالجات الدفع الأمريكية المدعومة؟',
          answer:
            'نتكامل مع Stripe وSquare وHeartland وجميع معالجات الدفع الأمريكية الكبرى. متوافق مع PCI DSS المستوى ١ مع تخزين البطاقات المرمّز وتقارير PCI الآلية.',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  Thai                                                              */
    /* ------------------------------------------------------------------ */
    th: {
      cityName: 'นิวยอร์ก',
      country: 'สหรัฐอเมริกา',
      heroTitle: 'ซอฟต์แวร์โรงแรมสำหรับที่พักในนิวยอร์กซิตี้',
      heroSubtitle:
        'ระบบ PMS บนคลาวด์สำหรับโรงแรมในนิวยอร์ก ที่พักบูทีค และเรสซิเดนซ์แบบพักระยะยาว การเรียกเก็บเงินเป็นดอลลาร์สหรัฐ การปฏิบัติตามภาษีโรงแรมของนิวยอร์ก และเครื่องมือจัดตารางแรงงานสหภาพ',
      highlights: [
        {
          title: 'การปฏิบัติตามภาษีโรงแรมนิวยอร์ก',
          description:
            'คำนวณภาษีการเข้าพักห้องโรงแรมของนิวยอร์ก ภาษีขายรัฐนิวยอร์ก และค่าธรรมเนียมเพิ่มเติมโดยอัตโนมัติในทุกใบแจ้งยอด',
        },
        {
          title: 'จัดตารางพนักงานสหภาพ',
          description:
            'เครื่องมือจัดตารางในตัวที่เคารพกฎแรงงานสหภาพ การติดตามล่วงเวลา และการจัดการกะสำหรับพนักงานโรงแรมสหภาพในนิวยอร์ก',
        },
        {
          title: 'เช็คอินปริมาณสูง',
          description:
            'รองรับเช็คอินมากกว่า 200 รายการต่อวันด้วยโหมดคีออสก์ด่วน การจัดการการมาถึงแบบกลุ่ม และอัลกอริทึมจัดสรรห้องอัตโนมัติ',
        },
        {
          title: 'การจัดการรายได้',
          description:
            'การกำหนดราคาแบบไดนามิกขับเคลื่อนด้วย AI ที่ปรับให้เหมาะกับรูปแบบอุปสงค์ของนิวยอร์ก การปรับราคาตามอีเวนต์ และการเปรียบเทียบราคาคู่แข่ง',
        },
      ],
      propertyTypes: [
        'โรงแรมบริการครบวงจร',
        'โรงแรมบูทีค',
        'ที่พักระยะยาว',
        'โรงแรมหรู',
        'โรงแรมคอนเวนชัน',
        'อะพาร์ทโฮเทล',
      ],
      popularAreas: [
        'มิดทาวน์แมนฮัตตัน',
        'ไทม์สแควร์',
        'โซโห',
        'บรูคลิน',
        'ย่านการเงิน',
        'อัปเปอร์อีสต์ไซด์',
        'เชลซี',
      ],
      compliance: [
        'ภาษีการเข้าพักโรงแรมนิวยอร์ก',
        'ระบบอัตโนมัติภาษีขายรัฐนิวยอร์ก',
        'การติดตามการเข้าถึง ADA',
        'รายงานความปลอดภัยจากอัคคีภัย NYC',
        'การปฏิบัติตาม PCI DSS',
      ],
      faqItems: [
        {
          question: 'HotelStack.io คำนวณภาษีโรงแรมนิวยอร์กอัตโนมัติหรือไม่?',
          answer:
            'ใช่ ระบบของเราจะใช้ภาษีการเข้าพักห้องโรงแรม NYC ($2 ต่อห้องต่อคืน บวก 5.875%) ภาษีขายรัฐนิวยอร์ก (4%) และค่าธรรมเนียมการขนส่งชานเมืองมหานครโดยอัตโนมัติกับทุกการจอง',
        },
        {
          question: 'รองรับเช็คอินปริมาณสูงในช่วงอีเวนต์ใหญ่ของนิวยอร์กได้หรือไม่?',
          answer:
            'แน่นอน โหมดเช็คอินด่วน การประมวลผลการมาถึงแบบกลุ่ม และการจัดสรรห้องอัตโนมัติรองรับเช็คอินมากกว่า 200 รายการต่อวัน เหมาะสำหรับที่พักใกล้ศูนย์ประชุมและสถานที่จัดงาน',
        },
        {
          question: 'รองรับข้อกำหนดการจัดตารางแรงงานสหภาพหรือไม่?',
          answer:
            'ใช่ HotelStack.io มีการจัดตารางที่ตระหนักถึงสหภาพ พร้อมกฎล่วงเวลาที่กำหนดค่าได้ ข้อกำหนดการพัก การกำหนดกะตามอาวุโส และรายงานการปฏิบัติตามสำหรับสหภาพโรงแรม NYC',
        },
        {
          question: 'รองรับผู้ประมวลผลการชำระเงินของสหรัฐรายใดบ้าง?',
          answer:
            'เราเชื่อมต่อกับ Stripe, Square, Heartland และผู้ประมวลผลการชำระเงินรายใหญ่ของสหรัฐทั้งหมด เป็นไปตาม PCI DSS ระดับ 1 พร้อมการจัดเก็บบัตรแบบโทเค็นและรายงาน PCI อัตโนมัติ',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  Spanish                                                           */
    /* ------------------------------------------------------------------ */
    es: {
      cityName: 'Nueva York',
      country: 'Estados Unidos',
      heroTitle: 'Software hotelero para propiedades en la ciudad de Nueva York',
      heroSubtitle:
        'PMS en la nube diseñado para hoteles de NYC, propiedades boutique y residencias de estancia prolongada. Facturación en USD, cumplimiento fiscal hotelero de NYC y herramientas de programación laboral sindical.',
      highlights: [
        {
          title: 'Cumplimiento fiscal hotelero de NYC',
          description:
            'Cálculo automático del impuesto de ocupación de habitaciones de hotel de NYC, impuesto estatal de ventas de NY y recargos municipales en cada folio.',
        },
        {
          title: 'Programación de personal sindicalizado',
          description:
            'Herramientas de programación integradas que respetan las normas laborales sindicales, seguimiento de horas extra y gestión de turnos para personal hotelero sindicalizado de NYC.',
        },
        {
          title: 'Check-in de alto volumen',
          description:
            'Gestione más de 200 check-ins diarios con modo kiosco exprés, gestión de llegadas grupales y algoritmos automáticos de asignación de habitaciones.',
        },
        {
          title: 'Gestión de ingresos',
          description:
            'Precios dinámicos impulsados por IA optimizados para patrones de demanda de NYC, ajustes de tarifas basados en eventos y comparación competitiva de tarifas.',
        },
      ],
      propertyTypes: [
        'Hoteles de servicio completo',
        'Hoteles boutique',
        'Estancia prolongada',
        'Hoteles de lujo',
        'Hoteles de convenciones',
        'Aparthoteles',
      ],
      popularAreas: [
        'Midtown Manhattan',
        'Times Square',
        'SoHo',
        'Brooklyn',
        'Distrito Financiero',
        'Upper East Side',
        'Chelsea',
      ],
      compliance: [
        'Impuesto de ocupación hotelera de NYC',
        'Automatización del impuesto estatal de ventas de NY',
        'Seguimiento de accesibilidad ADA',
        'Informes de seguridad contra incendios de NYC',
        'Cumplimiento de pagos PCI DSS',
      ],
      faqItems: [
        {
          question: '¿HotelStack.io calcula automáticamente los impuestos hoteleros de NYC?',
          answer:
            'Sí. Nuestro sistema aplica automáticamente el impuesto de ocupación de habitaciones de hotel de NYC ($2 por habitación por noche más 5,875%), el impuesto estatal de ventas de NY (4%) y el recargo de Transporte Metropolitano en cada reserva.',
        },
        {
          question: '¿Puede manejar altos volúmenes de check-in durante eventos importantes de NYC?',
          answer:
            'Por supuesto. Nuestro modo de check-in exprés, procesamiento de llegadas grupales y asignación automática de habitaciones pueden gestionar más de 200 check-ins diarios. Ideal para propiedades cerca de centros de convenciones.',
        },
        {
          question: '¿Soporta requisitos de programación laboral sindical?',
          answer:
            'Sí. HotelStack.io incluye programación con conciencia sindical con reglas configurables de horas extra, requisitos de descanso, asignación de turnos por antigüedad e informes de cumplimiento.',
        },
        {
          question: '¿Qué procesadores de pago estadounidenses se soportan?',
          answer:
            'Nos integramos con Stripe, Square, Heartland y todos los principales procesadores de pago de EE.UU. Cumplimiento PCI DSS Nivel 1 con almacenamiento tokenizado de tarjetas e informes PCI automatizados.',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  French                                                            */
    /* ------------------------------------------------------------------ */
    fr: {
      cityName: 'New York',
      country: 'États-Unis',
      heroTitle: "Logiciel hôtelier pour les établissements de New York",
      heroSubtitle:
        "PMS cloud conçu pour les hôtels de NYC, les établissements boutique et les résidences longue durée. Facturation en USD, conformité fiscale hôtelière de NYC et outils de planification du personnel syndiqué.",
      highlights: [
        {
          title: 'Conformité fiscale hôtelière de NYC',
          description:
            "Calcul automatique de la taxe d'occupation des chambres d'hôtel de NYC, de la taxe de vente de l'État de NY et des surtaxes municipales sur chaque relevé.",
        },
        {
          title: 'Planification du personnel syndiqué',
          description:
            "Outils de planification intégrés respectant les règles syndicales, le suivi des heures supplémentaires et la gestion des quarts pour le personnel hôtelier syndiqué de NYC.",
        },
        {
          title: 'Enregistrement à haut volume',
          description:
            "Gérez plus de 200 enregistrements par jour avec le mode kiosque express, la gestion des arrivées de groupe et les algorithmes d'attribution automatique des chambres.",
        },
        {
          title: 'Gestion des revenus',
          description:
            "Tarification dynamique pilotée par l'IA, optimisée pour les schémas de demande de NYC, ajustements tarifaires basés sur les événements et veille tarifaire concurrentielle.",
        },
      ],
      propertyTypes: [
        'Hôtels à service complet',
        'Hôtels boutique',
        'Séjour prolongé',
        'Hôtels de luxe',
        'Hôtels de congrès',
        'Aparthôtels',
      ],
      popularAreas: [
        'Midtown Manhattan',
        'Times Square',
        'SoHo',
        'Brooklyn',
        'Quartier financier',
        'Upper East Side',
        'Chelsea',
      ],
      compliance: [
        "Taxe d'occupation hôtelière de NYC",
        'Automatisation de la taxe de vente de NY',
        'Suivi de conformité ADA',
        'Rapports de sécurité incendie de NYC',
        'Conformité PCI DSS des paiements',
      ],
      faqItems: [
        {
          question: 'HotelStack.io calcule-t-il automatiquement les taxes hôtelières de NYC ?',
          answer:
            "Oui. Notre système applique automatiquement la taxe d'occupation des chambres de NYC (2 $ par chambre par nuit plus 5,875 %), la taxe de vente de l'État de NY (4 %) et la surtaxe de transport métropolitain à chaque réservation.",
        },
        {
          question: "Peut-il gérer de forts volumes d'enregistrement lors d'événements majeurs à NYC ?",
          answer:
            "Absolument. Notre mode d'enregistrement express, le traitement des arrivées de groupe et l'attribution automatique des chambres peuvent gérer plus de 200 enregistrements par jour.",
        },
        {
          question: 'Prend-il en charge les exigences de planification syndicale ?',
          answer:
            "Oui. HotelStack.io inclut une planification adaptée aux syndicats avec des règles d'heures supplémentaires configurables, des exigences de pause, l'attribution des quarts par ancienneté et des rapports de conformité.",
        },
        {
          question: 'Quels processeurs de paiement américains sont pris en charge ?',
          answer:
            'Nous intégrons Stripe, Square, Heartland et tous les principaux processeurs de paiement américains. Conformité PCI DSS Niveau 1 avec stockage tokenisé des cartes et rapports PCI automatisés.',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  German                                                            */
    /* ------------------------------------------------------------------ */
    de: {
      cityName: 'New York',
      country: 'Vereinigte Staaten',
      heroTitle: 'Hotelsoftware für Unterkünfte in New York City',
      heroSubtitle:
        'Cloud-PMS für NYC-Hotels, Boutique-Unterkünfte und Langzeitresidenzen. USD-Abrechnung, NYC-Hotelsteuer-Konformität und gewerkschaftliche Personalplanung.',
      highlights: [
        {
          title: 'NYC-Hotelsteuer-Konformität',
          description:
            'Automatische Berechnung der NYC-Hotelzimmer-Belegungssteuer, der Umsatzsteuer des Staates New York und der städtischen Zuschläge auf jedem Folio.',
        },
        {
          title: 'Gewerkschaftliche Personalplanung',
          description:
            'Integrierte Planungstools, die gewerkschaftliche Arbeitsregeln, Überstundenverfolgung und Schichtmanagement für gewerkschaftlich organisiertes NYC-Hotelpersonal berücksichtigen.',
        },
        {
          title: 'Hochvolumiger Check-in',
          description:
            'Verarbeiten Sie über 200 Check-ins pro Tag mit Express-Kiosk-Modus, Gruppenankunfts-Management und automatischen Zimmerzuweisungsalgorithmen.',
        },
        {
          title: 'Ertragsmanagement',
          description:
            'KI-gesteuerte dynamische Preisgestaltung, optimiert für NYC-Nachfragemuster, eventbasierte Ratenanpassungen und wettbewerbsfähige Ratenanalyse.',
        },
      ],
      propertyTypes: [
        'Full-Service-Hotels',
        'Boutique-Hotels',
        'Langzeitaufenthalt',
        'Luxushotels',
        'Kongresshotels',
        'Aparthotels',
      ],
      popularAreas: [
        'Midtown Manhattan',
        'Times Square',
        'SoHo',
        'Brooklyn',
        'Financial District',
        'Upper East Side',
        'Chelsea',
      ],
      compliance: [
        'NYC-Hotelbelegungssteuer',
        'Automatisierung der NY-Umsatzsteuer',
        'ADA-Barrierefreiheitsverfolgung',
        'NYC-Brandschutzberichte',
        'PCI-DSS-Zahlungskonformität',
      ],
      faqItems: [
        {
          question: 'Berechnet HotelStack.io die NYC-Hotelsteuern automatisch?',
          answer:
            'Ja. Unser System wendet automatisch die NYC-Hotelzimmer-Belegungssteuer (2 $ pro Zimmer pro Nacht plus 5,875 %), die Umsatzsteuer des Staates NY (4 %) und den Metropolitan-Nahverkehrszuschlag auf jede Buchung an.',
        },
        {
          question: 'Kann es hohe Check-in-Volumen bei NYC-Großveranstaltungen bewältigen?',
          answer:
            'Absolut. Unser Express-Check-in-Modus, die Gruppenankunftsverarbeitung und die automatische Zimmerzuweisung können über 200 Check-ins pro Tag verarbeiten.',
        },
        {
          question: 'Unterstützt es gewerkschaftliche Planungsanforderungen?',
          answer:
            'Ja. HotelStack.io umfasst gewerkschaftsbewusste Planung mit konfigurierbaren Überstundenregeln, Pausenanforderungen, Schichtzuweisung nach Seniorität und Compliance-Berichten.',
        },
        {
          question: 'Welche US-Zahlungsdienstleister werden unterstützt?',
          answer:
            'Wir integrieren Stripe, Square, Heartland und alle großen US-Zahlungsdienstleister. PCI DSS Level 1 konform mit tokenisierter Kartenspeicherung und automatisierten PCI-Berichten.',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  Japanese                                                          */
    /* ------------------------------------------------------------------ */
    ja: {
      cityName: 'ニューヨーク',
      country: 'アメリカ合衆国',
      heroTitle: 'ニューヨーク市の宿泊施設向けホテルソフトウェア',
      heroSubtitle:
        'NYCのホテル、ブティックプロパティ、長期滞在レジデンス向けに構築されたクラウドPMS。USD請求、NYC宿泊税コンプライアンス、労働組合スケジューリングツール対応。',
      highlights: [
        {
          title: 'NYC宿泊税コンプライアンス',
          description:
            'NYCホテル客室占有税、NY州売上税、市のサーチャージをすべてのフォリオで自動計算します。',
        },
        {
          title: '労働組合スタッフスケジューリング',
          description:
            '労働組合の規則を尊重した組み込みスケジューリングツール。残業追跡とNYC組合ホテルスタッフのシフト管理に対応。',
        },
        {
          title: '大量チェックイン処理',
          description:
            'エクスプレスキオスクモード、グループ到着管理、自動客室割当アルゴリズムで1日200件以上のチェックインを処理。',
        },
        {
          title: '収益管理',
          description:
            'NYC需要パターンに最適化されたAI駆動のダイナミックプライシング、イベントベースの料金調整、競合料金比較。',
        },
      ],
      propertyTypes: [
        'フルサービスホテル',
        'ブティックホテル',
        '長期滞在施設',
        'ラグジュアリーホテル',
        'コンベンションホテル',
        'アパートホテル',
      ],
      popularAreas: [
        'ミッドタウン・マンハッタン',
        'タイムズスクエア',
        'ソーホー',
        'ブルックリン',
        'ファイナンシャル・ディストリクト',
        'アッパー・イーストサイド',
        'チェルシー',
      ],
      compliance: [
        'NYC宿泊占有税',
        'NY州売上税自動化',
        'ADAアクセシビリティ追跡',
        'NYC防火安全報告',
        'PCI DSS決済コンプライアンス',
      ],
      faqItems: [
        {
          question: 'HotelStack.ioはNYCホテル税を自動計算しますか？',
          answer:
            'はい。当システムはNYCホテル客室占有税（1室1泊あたり2ドル+5.875%）、NY州売上税（4%）、首都圏通勤交通サーチャージをすべての予約に自動適用します。',
        },
        {
          question: 'NYCの大型イベント時の大量チェックインに対応できますか？',
          answer:
            'もちろんです。エクスプレスチェックインモード、グループ到着処理、自動客室割当で1日200件以上のチェックインを処理できます。コンベンションセンター近隣の施設に最適です。',
        },
        {
          question: '労働組合のスケジューリング要件に対応していますか？',
          answer:
            'はい。HotelStack.ioは組合対応スケジューリングを搭載し、設定可能な残業ルール、休憩要件、年功序列ベースのシフト割当、コンプライアンスレポートに対応しています。',
        },
        {
          question: '米国のどの決済プロセッサに対応していますか？',
          answer:
            'Stripe、Square、Heartland、および米国の主要決済プロセッサすべてと統合しています。トークン化カードストレージと自動PCIレポートでPCI DSSレベル1準拠。',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  Chinese (Simplified)                                              */
    /* ------------------------------------------------------------------ */
    zh: {
      cityName: '纽约',
      country: '美国',
      heroTitle: '纽约市酒店物业管理软件',
      heroSubtitle:
        '专为纽约酒店、精品物业和长住公寓打造的云端PMS。美元计费、纽约酒店税务合规和工会排班工具。',
      highlights: [
        {
          title: '纽约酒店税务合规',
          description:
            '自动计算纽约市酒店客房占用税、纽约州销售税和市政附加费，体现在每张账单中。',
        },
        {
          title: '工会员工排班',
          description:
            '内置排班工具，遵循工会劳动规则，支持加班追踪和纽约工会酒店员工的轮班管理。',
        },
        {
          title: '高容量入住登记',
          description:
            '通过快速自助终端模式、团队到达管理和自动房间分配算法，每天处理200+次入住登记。',
        },
        {
          title: '收益管理',
          description:
            '针对纽约需求模式优化的AI驱动动态定价、基于活动的价格调整和竞争对手价格监控。',
        },
      ],
      propertyTypes: [
        '全服务酒店',
        '精品酒店',
        '长住酒店',
        '豪华酒店',
        '会议酒店',
        '公寓式酒店',
      ],
      popularAreas: [
        '中城曼哈顿',
        '时代广场',
        '苏豪区',
        '布鲁克林',
        '金融区',
        '上东区',
        '切尔西',
      ],
      compliance: [
        '纽约市酒店占用税',
        '纽约州销售税自动化',
        'ADA无障碍追踪',
        '纽约市消防安全报告',
        'PCI DSS支付合规',
      ],
      faqItems: [
        {
          question: 'HotelStack.io能自动计算纽约酒店税吗？',
          answer:
            '是的。我们的系统自动对每笔预订应用纽约市酒店客房占用税（每间每晚2美元加5.875%）、纽约州销售税（4%）和大都会通勤交通附加费。',
        },
        {
          question: '能否应对纽约大型活动期间的高入住登记量？',
          answer:
            '当然可以。我们的快速入住模式、团队到达处理和自动房间分配每天可处理200+次入住登记，非常适合会展中心附近的物业。',
        },
        {
          question: '是否支持工会排班要求？',
          answer:
            '是的。HotelStack.io包含工会感知排班功能，支持可配置的加班规则、休息要求、按资历分配轮班和合规报告。',
        },
        {
          question: '支持哪些美国支付处理商？',
          answer:
            '我们集成了Stripe、Square、Heartland及所有主要美国支付处理商。符合PCI DSS一级标准，支持令牌化卡片存储和自动PCI报告。',
        },
      ],
    },

    /* ------------------------------------------------------------------ */
    /*  Hindi                                                             */
    /* ------------------------------------------------------------------ */
    hi: {
      cityName: 'न्यूयॉर्क',
      country: 'संयुक्त राज्य अमेरिका',
      heroTitle: 'न्यूयॉर्क सिटी प्रॉपर्टीज के लिए होटल सॉफ्टवेयर',
      heroSubtitle:
        'NYC होटलों, बुटीक प्रॉपर्टीज और एक्सटेंडेड-स्टे रेजिडेंस के लिए बनाया गया क्लाउड PMS। USD बिलिंग, NYC होटल टैक्स अनुपालन और यूनियन लेबर शेड्यूलिंग टूल्स।',
      highlights: [
        {
          title: 'NYC होटल टैक्स अनुपालन',
          description:
            'हर फोलियो पर NYC होटल रूम ऑक्यूपेंसी टैक्स, NY राज्य बिक्री कर और शहर सरचार्ज की स्वचालित गणना।',
        },
        {
          title: 'यूनियन स्टाफ शेड्यूलिंग',
          description:
            'NYC यूनियन होटल स्टाफ के लिए यूनियन श्रम नियमों का पालन करने वाले बिल्ट-इन शेड्यूलिंग टूल्स, ओवरटाइम ट्रैकिंग और शिफ्ट प्रबंधन।',
        },
        {
          title: 'हाई-वॉल्यूम चेक-इन',
          description:
            'एक्सप्रेस कियोस्क मोड, ग्रुप अराइवल मैनेजमेंट और ऑटोमेटेड रूम असाइनमेंट एल्गोरिदम के साथ प्रतिदिन 200+ चेक-इन संभालें।',
        },
        {
          title: 'राजस्व प्रबंधन',
          description:
            'NYC डिमांड पैटर्न के लिए अनुकूलित AI-संचालित डायनामिक प्राइसिंग, इवेंट-आधारित रेट एडजस्टमेंट और प्रतिस्पर्धी रेट शॉपिंग।',
        },
      ],
      propertyTypes: [
        'फुल-सर्विस होटल',
        'बुटीक होटल',
        'एक्सटेंडेड स्टे',
        'लग्जरी होटल',
        'कन्वेंशन होटल',
        'अपार्टहोटल',
      ],
      popularAreas: [
        'मिडटाउन मैनहट्टन',
        'टाइम्स स्क्वेयर',
        'सोहो',
        'ब्रुकलिन',
        'फाइनेंशियल डिस्ट्रिक्ट',
        'अपर ईस्ट साइड',
        'चेल्सी',
      ],
      compliance: [
        'NYC होटल ऑक्यूपेंसी टैक्स',
        'NY राज्य बिक्री कर ऑटोमेशन',
        'ADA एक्सेसिबिलिटी ट्रैकिंग',
        'NYC अग्नि सुरक्षा रिपोर्टिंग',
        'PCI DSS भुगतान अनुपालन',
      ],
      faqItems: [
        {
          question: 'क्या HotelStack.io NYC होटल टैक्स स्वचालित रूप से कैलकुलेट करता है?',
          answer:
            'हां। हमारा सिस्टम हर बुकिंग पर स्वचालित रूप से NYC होटल रूम ऑक्यूपेंसी टैक्स ($2 प्रति कमरा प्रति रात प्लस 5.875%), NY राज्य बिक्री कर (4%) और मेट्रोपॉलिटन कम्यूटर ट्रांसपोर्टेशन सरचार्ज लागू करता है।',
        },
        {
          question: 'क्या यह NYC के बड़े इवेंट्स के दौरान हाई चेक-इन वॉल्यूम संभाल सकता है?',
          answer:
            'बिल्कुल। हमारा एक्सप्रेस चेक-इन मोड, ग्रुप अराइवल प्रोसेसिंग और ऑटोमेटेड रूम असाइनमेंट प्रतिदिन 200+ चेक-इन संभाल सकता है। कन्वेंशन सेंटर के नजदीक प्रॉपर्टीज के लिए आदर्श।',
        },
        {
          question: 'क्या यह यूनियन लेबर शेड्यूलिंग आवश्यकताओं को सपोर्ट करता है?',
          answer:
            'हां। HotelStack.io में यूनियन-अवेयर शेड्यूलिंग शामिल है जिसमें कॉन्फ़िगर करने योग्य ओवरटाइम नियम, ब्रेक आवश्यकताएं, सीनियरिटी-आधारित शिफ्ट असाइनमेंट और अनुपालन रिपोर्टिंग है।',
        },
        {
          question: 'कौन से US पेमेंट प्रोसेसर सपोर्टेड हैं?',
          answer:
            'हम Stripe, Square, Heartland और सभी प्रमुख US पेमेंट प्रोसेसर के साथ इंटीग्रेट करते हैं। टोकनाइज्ड कार्ड स्टोरेज और ऑटोमेटेड PCI रिपोर्टिंग के साथ PCI DSS लेवल 1 कम्प्लायंट।',
        },
      ],
    },
  },
};
